<script setup lang="ts">
/**
 * The Ultra (live) dictation mic (MAR-1423; design decided by Alex 2026-10-28 & 2026-11-03, on the ticket).
 *
 * Idle: a Fresco Stone (`var(--color-secondary)`) squircle (`0.625rem` / `0.5rem` in `sm`, matching
 * the adjacent `.chat-send` button) with a `1px` `var(--color-input)` border and `var(--color-accent-ink)`
 * icon. While dictating, the SAME button morphs into a solid `var(--color-accent)` (`#B84816`) squircle
 * pill, "● Dictando 0:42", with a pulsing `var(--color-accent-bright)` (`#F8F5C2` Pale Mineral) dot,
 * and morphs back on stop: width, colour and border are CSS transitions, not a swap of two elements.
 * With `prefers-reduced-motion` it swaps instantly. The timer is ELAPSED time only — never "/ 10:00":
 * Alex judged the cap confusing to show, and the 10-minute stop is enforced regardless (client and
 * gateway). The timer is `aria-hidden` so a screen reader is not told the time every second;
 * `aria-pressed` and the label carry the state.
 *
 * Plans without live dictation, and a live attempt that fell back to the browser's own recognition,
 * keep the plain mic in `ChatInput.vue` — this component is only the Ultra look.
 */
import { Loader2, Mic } from 'lucide-vue-next'

const props = withDefaults(defineProps<{
  /** Dictating (once the Gemini Live session is ready until stopping begins). */
  active: boolean
  /** Opening the microphone and Gemini Live WebSocket session (`ready` not yet received). */
  connecting?: boolean
  /** Draining the final transcript after microphone capture stopped. */
  stopping?: boolean
  /** Elapsed time of the current dictation. */
  elapsedMs: number
  /** `sm` matches the compact composer's 28 px controls, `md` the box composer's 32 px. */
  size?: 'sm' | 'md'
}>(), {
  connecting: false,
  stopping: false,
  size: 'md',
})

const emit = defineEmits<{ toggle: [] }>()
const { t } = useI18n()

const elapsed = computed(() => {
  const seconds = Math.max(0, Math.floor(props.elapsedMs / 1000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
})
const label = computed(() => ((props.active || props.connecting || props.stopping) ? t('chat.stopDictation') : t('chat.ultraDictation')))
</script>

<template>
  <button
    type="button"
    data-testid="ultra-dictation-mic"
    class="ultra-mic"
    :class="[active && 'ultra-mic--on', size === 'sm' && 'ultra-mic--sm']"
    :disabled="stopping"
    :title="label"
    :aria-label="label"
    :aria-pressed="active ? 'true' : 'false'"
    :aria-busy="connecting || stopping ? 'true' : undefined"
    @click="emit('toggle')"
  >
    <span v-if="active" class="ultra-mic__live">
      <span class="ultra-mic__dot" aria-hidden="true" />
      <span>{{ t('chat.ultraDictating') }}</span>
      <span data-testid="ultra-dictation-timer" class="ultra-mic__timer" aria-hidden="true">{{ elapsed }}</span>
    </span>
    <Loader2
      v-else-if="connecting || stopping"
      data-testid="ultra-dictation-spinner"
      class="ultra-mic__icon animate-spin"
      aria-hidden="true"
    />
    <Mic v-else class="ultra-mic__icon" aria-hidden="true" />
  </button>
</template>

<style scoped>
.ultra-mic {
  position: relative;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 2rem;
  width: 2rem;
  padding: 0;
  border-radius: 0.625rem;
  border: 1px solid var(--color-input);
  overflow: hidden;
  white-space: nowrap;
  cursor: pointer;
  color: var(--color-accent-ink);
  background-color: var(--color-secondary);
  transition:
    width 0.22s ease-out,
    background-color 0.22s ease-out,
    border-color 0.22s ease-out,
    color 0.22s ease-out;
}
.ultra-mic--sm {
  height: 1.75rem;
  width: 1.75rem;
  border-radius: 0.5rem;
}

.ultra-mic:hover:not(.ultra-mic--on):not(:disabled) {
  background-color: var(--color-accent-lighter);
}
.ultra-mic:disabled {
  cursor: default;
  opacity: 0.65;
}
.ultra-mic:focus-visible {
  outline: 2px solid var(--color-ring);
  outline-offset: 2px;
}

.ultra-mic--on {
  width: 7.5rem;
  border-color: transparent;
  color: var(--color-on-accent);
  background-color: var(--color-accent);
}

.ultra-mic__icon {
  height: 1rem;
  width: 1rem;
}
.ultra-mic--sm .ultra-mic__icon {
  height: 0.875rem;
  width: 0.875rem;
}

.ultra-mic__live {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0 0.75rem 0 0.625rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
}
.ultra-mic__dot {
  height: 0.4375rem;
  width: 0.4375rem;
  flex: none;
  border-radius: 9999px;
  background-color: var(--color-accent-bright);
  animation: ultra-mic-pulse 1.2s ease-in-out infinite;
}
.ultra-mic__timer {
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  opacity: 0.85;
}

@keyframes ultra-mic-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

@media (prefers-reduced-motion: reduce) {
  .ultra-mic {
    transition: none;
  }
  .ultra-mic__dot {
    animation: none;
  }
}
</style>
